/**
 * The words on the Aperio plan step (components/AperioPlanOptions.tsx):
 * three short plan cards, one shared "Every plan includes" list, the per-
 * vehicle price breakdown and the fine print.
 *
 * Kept short on purpose (owner, 2026-09-24: "are we too wordy or what").
 * The shared list and the disclosure are the marketing site's own lines
 * (paths relative to `aperio/site/src/pages/`), so the booking step never
 * promises anything the site does not; tests/aperioPlanOffer.spec.tsx checks
 * they are still on the site. The plan names and the one-liners are the
 * owner's.
 *
 * The words themselves live in the portal's locale files, `plans.*` in
 * `src/lang/{en,fr,es}/portal.json` (QA 2026-09-25 #6): English is the
 * source, French and Spanish are the site's own translations of the same
 * lines (lang-{fr,es}*.js). Every helper here returns the language on
 * screen; the `PLAN_*` constants are the English text, for the site check.
 *
 * The admin-written plan descriptions in the database are NOT shown under
 * the preset: they carry stale figures ("$15 per service visit", "/month
 * equivalent").
 *
 * Only the Aperio preset renders this.
 */

import i18n from '../i18n';
import { formatMoney, formatWholeDollars } from '../i18n/format';
import en from '../lang/en/portal.json';
import type { PlanPriceView } from './aperioPlans';
import { ServiceNameKeys } from './serviceKeys';

/** Which of the three plans a catalogue row belongs to. */
export type AperioPlanKind = 'yearly' | 'monthly' | 'allInclusive';

/** Card order: Yearly first. */
export const APERIO_PLAN_ORDER: AperioPlanKind[] = [
  'yearly',
  'monthly',
  'allInclusive',
];

const translate = i18n.t.bind(i18n) as (
  key: string,
  vars?: Record<string, unknown>,
) => string;
const t = (key: string, vars?: Record<string, unknown>): string => (
  translate(key, vars)
);

/** The card titles, in English (the site's plan names). */
export const APERIO_PLAN_NAME: Record<AperioPlanKind, string> = en.plans.name;

/** A card title in the language on screen: Yearly / Annuel / Anual. */
export const aperioPlanName = (kind: AperioPlanKind): string => (
  t(`plans.name.${kind}`)
);

/**
 * The plan a service row belongs to, from either key of its pair (the
 * visible "subscribe to" onboarding row or the hidden plan row).
 */
export function aperioPlanKind(nameKey?: string | null): AperioPlanKind | null {
  switch (nameKey) {
    case ServiceNameKeys.subscribeToAnnual:
    case ServiceNameKeys.subscriptionPlanAnnual:
      return 'yearly';
    case ServiceNameKeys.subscribeToMonthly:
    case ServiceNameKeys.subscriptionPlanMonthly:
      return 'monthly';
    case ServiceNameKeys.subscribeToAllInclusiveAnnual:
    case ServiceNameKeys.subscriptionPlanAnnualAllInclusive:
      return 'allInclusive';
    default:
      return null;
  }
}

/** "Yearly plan" etc., for the summary bar and the order summary. */
export const aperioPlanLabel = (nameKey?: string | null): string | null => {
  const kind = aperioPlanKind(nameKey);
  return kind ? t(`plans.label.${kind}`) : null;
};

/** "$199" for whole dollars, "$21.91" otherwise ("199 $", "21,91 $" in FR). */
export const planMoney = (cents: number): string => (
  Math.round(cents) % 100 === 0
    ? formatWholeDollars(Math.round(cents / 100))
    : formatMoney(Math.round(cents) / 100)
);

/** The one line under each card's price. */
export const planOneLiner = (
  kind: AperioPlanKind,
  opts: {
    /** Yearly: what it saves against the monthly plan, in cents. */
    savingsCents?: number | null;
    /** Monthly: the number of payments. */
    instalments?: number;
    /** All-Inclusive: its fee for the visits inside the allowance. */
    feeCents?: number;
    visitsIncluded?: number | null;
  } = {},
): string => {
  if (kind === 'yearly') {
    return opts.savingsCents && opts.savingsCents > 0
      ? t('plans.oneLiner.yearlySave', { amount: planMoney(opts.savingsCents) })
      : t('plans.oneLiner.yearly');
  }
  if (kind === 'monthly') {
    return t('plans.oneLiner.monthly', { count: opts.instalments ?? 12 });
  }
  return opts.visitsIncluded
    ? t('plans.oneLiner.allInclusiveVisits', {
      fee: planMoney(opts.feeCents ?? 0),
      visits: opts.visitsIncluded,
    })
    : t('plans.oneLiner.allInclusive', { fee: planMoney(opts.feeCents ?? 0) });
};

/**
 * The price line under a card's figure in a price view (owner, 2026-10): an
 * i18n key and its values, for <Trans> (the key may carry a <b>). Null where
 * the card says only its one-liner (All-Inclusive, by the year). All-
 * Inclusive keeps its one-liner after this line in both views.
 *
 *   Yearly, month:  "Billed $259 once a year. You save $50."
 *   Yearly, year:   "Paid once. You save $50 vs monthly."
 *   Monthly, month: "12 monthly payments. $309 over the year."
 *   Monthly, year:  "Paid as 12 × $25.75." (uneven: "... 11 × $21.91, then
 *                   $21.99.")
 *   All-Incl, month: "Billed $420 once a year."
 */
export interface PlanPriceLine {
  key: string;
  values: Record<string, unknown>;
}

export const planPriceLine = (
  kind: AperioPlanKind,
  view: PlanPriceView,
  opts: {
    /** The plan row's price: the year, or the monthly payment. */
    planCents: number;
    /** Yearly: what it saves against the monthly plan, in cents. */
    savingsCents?: number | null;
    /** Monthly: the number of payments, the last one and their total. */
    instalments?: number;
    lastInstalmentCents?: number;
    termTotalCents?: number;
  },
): PlanPriceLine | null => {
  const saves = !!opts.savingsCents && opts.savingsCents > 0;
  const amount = saves ? planMoney(opts.savingsCents as number) : undefined;
  if (kind === 'monthly') {
    const count = opts.instalments ?? 12;
    if (view === 'month') {
      return {
        key: 'plans.view.monthlyTotal',
        values: {
          count,
          total: planMoney(opts.termTotalCents ?? opts.planCents * count),
        },
      };
    }
    const last = opts.lastInstalmentCents ?? opts.planCents;
    return last === opts.planCents || count < 2
      ? {
        key: 'plans.view.paidAs',
        values: { count, amount: planMoney(opts.planCents) },
      }
      : {
        key: 'plans.view.paidAsLast',
        values: {
          count: count - 1,
          amount: planMoney(opts.planCents),
          last: planMoney(last),
        },
      };
  }
  if (view === 'year') {
    if (kind === 'allInclusive') return null;
    return saves
      ? { key: 'plans.view.paidOnceSave', values: { amount } }
      : { key: 'plans.view.paidOnce', values: {} };
  }
  const price = planMoney(opts.planCents);
  return kind === 'yearly' && saves
    ? { key: 'plans.view.billedYearlySave', values: { price, amount } }
    : { key: 'plans.view.billedYearly', values: { price } };
};

/** A card's check line: the item, and an optional short detail under it. */
export interface PlanCheck {
  text: string;
  detail?: string;
  /** Not in this plan, at a price: drawn with a dash, not a check. */
  extra?: boolean;
}

/**
 * The visit-fee check line. `firstVisitIncluded`: the onboarding includes
 * the first visit's fee, so a paid fee is quoted "after your first".
 */
export const planFeeCheck = (
  feeCents: number,
  highUseCents: number | null,
  visitsIncluded: number | null,
  firstVisitIncluded = false,
): PlanCheck => {
  const escalates = !!highUseCents && !!visitsIncluded
    && highUseCents !== feeCents;
  const perVisit = firstVisitIncluded && feeCents > 0
    ? t('plans.check.feeAfterFirst', { fee: planMoney(feeCents) })
    : t('plans.check.feePerVisit', { fee: planMoney(feeCents) });
  if (!escalates) return { text: perVisit };
  return {
    text: feeCents === 0
      ? t('plans.check.feeFirstVisits', {
        fee: planMoney(0),
        visits: visitsIncluded,
      })
      : perVisit,
    detail: t('plans.check.feeAfter', {
      fee: planMoney(highUseCents as number),
      visit: (visitsIncluded as number) + 1,
    }),
  };
};

/**
 * The onboarding check line; `includesFee` when it covers the first visit's
 * mobile fee (said where the money is, so nobody reads it as a second fee).
 */
export const planOnboardingCheck = (
  cents: number,
  includesFee = false,
): PlanCheck => ({
  text: t('plans.check.onboarding', { amount: planMoney(cents) }),
  ...(includesFee && { detail: t('plans.check.onboardingIncludesFee') }),
});

/** All-Inclusive: the storage check line (owner-confirmed). */
export const planStorageCheck = (): PlanCheck => ({
  text: t('plans.check.storage'),
  detail: t('plans.check.storageDetail'),
});

/**
 * pricing.html, add-ons: "Tire storage for plan members, per season — $60".
 * The other plans' storage line, so the three cards compare line by line.
 */
export const PLAN_STORAGE_PER_SEASON_CENTS = 6000;
export const planStorageExtra = (cents: number): PlanCheck => ({
  text: t('plans.check.storageExtra', { amount: planMoney(cents) }),
  extra: true,
});

export const planChooseLabel = (kind: AperioPlanKind): string => (
  t('plans.choose', { name: aperioPlanName(kind) })
);

/**
 * The site's included grid (pricing.html #included, and the same grid on
 * home.html since round 2, 2026-10-25), in the site's order: every service the
 * plan covers (owner, 2026-09-24: the card must say what is included, all of
 * it). Each name is the site's row name; the detail is the site's own small
 * print for that row. Keys into `plans.includes.*`.
 */
export const PLAN_INCLUDE_KEYS = [
  'oil',
  'rotation',
  'swap',
  'brakes',
  'safety',
  'airFilters',
  'battery',
  'photos',
] as const;

type IncludeKey = typeof PLAN_INCLUDE_KEYS[number];

const INCLUDES_EN = en.plans.includes as Record<
IncludeKey, { name: string; detail?: string }
>;

/** The shared list's rows in English (the site check). */
export const PLAN_INCLUDES: string[] = PLAN_INCLUDE_KEYS
  .map((key) => INCLUDES_EN[key].name);

/** The shared list, in the language on screen. */
export const planIncludes = (): { name: string; detail?: string }[] => (
  PLAN_INCLUDE_KEYS.map((key) => ({
    name: t(`plans.includes.${key}.name`),
    detail: INCLUDES_EN[key].detail
      ? t(`plans.includes.${key}.detail`)
      : undefined,
  }))
);

/** pricing.html, the `price-card` list's last line (owner's disclosure). */
export const PLAN_DISCLOSURE = en.plans.disclosure;

/** home.html, the price card's secondary link. */
export const PLAN_PAY_PER_VISIT = en.plans.payPerVisit;

/** The disclosure that expands the per-vehicle breakdown. */
export const planBreakdownSummary = (model?: string | null): string => {
  const m = (model || '').trim();
  return m
    ? t('plans.breakdownSummaryModel', { model: m })
    : t('plans.breakdownSummary');
};

/** The fine print, first line: currency for this address. */
export const planFinePrint = (
  countryCode?: string | null,
  prepayPercent?: number,
): string => [
  t('plans.fine.commitment'),
  countryCode === 'US' ? t('plans.fine.usd') : t('plans.fine.cad'),
  prepayPercent && prepayPercent > 0
    ? t('plans.fine.prepay', { percent: prepayPercent })
    : '',
].filter(Boolean).join(' ');
